<script setup>
// Секция «Воронка» для отчёта «Диалоги». Самодостаточна: сама тянет
// AnalyticsAPI.overview за последние 30 дней и рисует воронку
// created → replied → resolved через BarRow с процентами.
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BarRow from 'dashboard/components-next/analytics/BarRow.vue';
import AnalyticsAPI from 'dashboard/api/analytics';

const { t } = useI18n();

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const PERIOD_DAYS = 30;

const isFetching = ref(false);
const overview = ref(null);

const funnelSteps = computed(() => {
  const funnel = overview.value?.funnel;
  if (!funnel) return [];
  const created = funnel.created || 0;
  const steps = [
    {
      key: 'created',
      label: t('ANALYTICS.FUNNEL.CREATED'),
      value: funnel.created || 0,
    },
    {
      key: 'replied',
      label: t('ANALYTICS.FUNNEL.REPLIED'),
      value: funnel.replied || 0,
    },
    {
      key: 'resolved',
      label: t('ANALYTICS.FUNNEL.RESOLVED'),
      value: funnel.resolved || 0,
    },
  ];
  return steps.map(step => ({
    ...step,
    pct: created > 0 ? Math.round((step.value / created) * 100) : 0,
  }));
});

const fetchFunnel = async () => {
  isFetching.value = true;
  try {
    const until = new Date();
    const since = new Date(until.getTime() - PERIOD_DAYS * DAY_IN_MS);
    const response = await AnalyticsAPI.overview({
      since: since.toISOString(),
      until: until.toISOString(),
    });
    overview.value = response.data;
  } catch {
    overview.value = null;
  } finally {
    isFetching.value = false;
  }
};

onMounted(() => fetchFunnel());
</script>

<template>
  <section class="flex flex-col gap-3 pt-6 mt-6 border-t border-n-weak">
    <h3 class="text-base font-medium text-n-slate-12">
      {{ t('ANALYTICS.FUNNEL.TITLE') }}
    </h3>
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-6 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="funnelSteps.length" class="flex flex-col gap-3">
      <BarRow
        v-for="step in funnelSteps"
        :key="step.key"
        :label="step.label"
        :value="step.value"
        :max="funnelSteps[0] ? funnelSteps[0].value : 0"
        :display-value="`${step.value} (${step.pct}%)`"
      />
    </div>
    <p v-else class="text-sm text-n-slate-11">
      {{ t('ANALYTICS.EMPTY') }}
    </p>
  </section>
</template>
